import { useState, useEffect, useMemo, useCallback } from "react";
import { ApolloError, useQuery, useMutation } from "@apollo/client";
import {
    getCorporationAndMillBackOffice,
    getCorporations,
    insertCorporationWithAdmin,
    upsertCorporationData,
    activateCorporateModuleLicense,
    getCorporationDetail,
    getCountries,
    getStatesByCountryId,
    getCitiesByStateId,
    GetCorporationAdmins,
    deactivateOrReactivateCorporateModuleLicense,
    updateCorporateRcaEnabled,
    getCorporationCountryCode,
    upsertCorporationWithAdminUser,
    getCorporateAllDivisionAllRegionsAllMills,
    getAllKeySuccessFactors,
    getAllFailureTypes,
    updateCorporateDashboardFeature,
} from "../../api/queries/corporationsAPI";
import {
    ICity,
    ICorporation,
    ICountry,
    IMill,
    IState,
} from "../../pages/Osorno/OsornoSetupDashboard/Corporations/CorporationsAPIInterfaces";
import {
    ICorporationsEntity,
    IDowntimeSummaryByFailureCode,
    IDowntimeSummaryByFailureType,
    IDowntimeSummaryBySecondaryFailureCodeTotals,
    IDowntimeSummaryTotalsByCodeAllMills,
    IDowntimeSummaryTotalsByTypeAllMills,
    IUser,
    TrailingTrendPeriodOptionsEnum,
} from "./CorporationsEntityInterfaces";
import { getCorporateDivisionsRegionsMills } from "api/queries/AppDrawerAPI";
import { ICorporateDashboardFilters } from "pages/Corporate/CorporateDashboard/useCorporateDashboardFilters";
import { SummaryOption, allElements } from "constants/constants";
import { getEndOfDateRange, getStartOfDateRange } from "utilities/utilities";
import {
    getDowntimeSummaryByFailureCodeCorporateDashboardFiltered,
    getDowntimeSummaryByFailureTypeCorporateDashboard,
    getDowntimeSummaryBySecondaryFailureCodeCorporateDashboardFiltered,
    getDowntimeSummaryFailureCodeByMillsCorporateDashboardFiltered,
    getDowntimeSummaryFailureTypeByMillsCorporateDashboardFiltered,
    getDowntimeSummarySecondaryFailureCodeByMillsCorporateDashboardFiltered,
    IGetDowntimeSummaryByFailureTypeCorporateDashboardResponse,
    IGetDowntimeSummaryByFailureCodeCorporateDashboardResponse,
    IGetDowntimeSummaryBySecondaryFailureCodeCorporateDashboardResponse,
    IGetDowntimeSummaryFailureCodeByMillsCorporateDashboardFilteredResponse,
    IGetDowntimeSummaryFailureTypeByMillsCorporateDashboardFilteredResponse,
} from "api/queries/corporateDashboardAPI";
import moment from "moment";
import { IKeySuccessFactor } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

export const CorporationsEntity: ICorporationsEntity = {
    GetCorporations: (user_type_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCorporations, { variables: { user_type_id } });
        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, apolloQueryError, refetch };
    },
    GetCorporationCountryCode: (corporate_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCorporationCountryCode, { variables: { corporate_id } });
        const [corporationCountryCode, setCorporationCountryCode] = useState<string>("");
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const corporationCountryCode: string = data.corporate[0].country?.code;
                setCorporationCountryCode(corporationCountryCode);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporationCountryCode, apolloQueryError, refetch };
    },
    GetCountries: () => {
        const { loading, error, data, refetch } = useQuery(getCountries);
        const [countries, setCountries] = useState<ICountry[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const countriesArray: ICountry[] = data.country;
                setCountries(countriesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, countries, apolloQueryError, refetch };
    },
    GetStates: (country_id: number) => {
        const { loading, error, data, refetch } = useQuery(getStatesByCountryId, { variables: { country_id } });
        const [states, setStates] = useState<IState[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const statesArray: IState[] = data.state;
                setStates(statesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, states, apolloQueryError, refetch };
    },
    GetCities: (state_id: number) => {
        const { loading, error, data, refetch } = useQuery(getCitiesByStateId, { variables: { state_id } });
        const [cities, setCities] = useState<ICity[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const citiesArray: ICity[] = data.city;
                setCities(citiesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, cities, apolloQueryError, refetch };
    },
    InsertCorporationWithAdmin: () => {
        const [addCorporationWithAdmin, { error }] = useMutation(insertCorporationWithAdmin);

        return { apolloMutationError: error, addCorporationWithAdmin };
    },
    UpsertCorporationWithAdminMaderaBackoffice: () => {
        const [upsertCorporationWithAdmin, { error }] = useMutation(upsertCorporationWithAdminUser);

        return { apolloMutationError: error, upsertCorporationWithAdmin };
    },
    GetCorporationAndMillBackOffice: (millId?: number) => {
        const { loading, error, data, refetch } = useQuery(
            getCorporationAndMillBackOffice,
            useMemo(
                () => ({
                    fetchPolicy: "no-cache",
                    variables: {
                        where: {
                            ...(millId
                                ? {
                                      mill_id: { _eq: millId },
                                  }
                                : {}),
                        },
                        currentDate: new Date().toISOString(),
                    },
                }),
                [millId],
            ),
        );

        return {
            loading,
            corporation: data?.corporate?.[0],
            mill: data?.mill?.[0],
            error,
            refetch,
        };
    },
    UpsertCorporateDataOnboardingBO: () => {
        const [upsertCorporateData, { error }] = useMutation(upsertCorporationData);

        return { apolloMutationError: error, upsertCorporateData };
    },
    GetMenuCorporateDivisionsRegionsMills: () => {
        const { loading, data, error, refetch } = useQuery(getCorporateDivisionsRegionsMills);

        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);
        const [millsWithNoDivision, setMillsWithNoDivision] = useState<{ name: string; mill_id: number }[]>([]);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (data?.mill) {
                const millsArray: { name: string; mill_id: number }[] = data.mill;
                setMillsWithNoDivision(millsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, millsWithNoDivision, error: apolloQueryError, refetch };
    },
    ActivateCorporateModuleLicenseOsornoBO: () => {
        const [activateCorporateModule, { error }] = useMutation(activateCorporateModuleLicense);

        return { apolloMutationError: error, activateCorporateModule };
    },
    DeactivateOrReactivateCorporateModuleLicenseOsornoBO: () => {
        const [deactivateOrReactivateCorporateModule, { error }] = useMutation(
            deactivateOrReactivateCorporateModuleLicense,
        );

        return { apolloMutationError: error, deactivateOrReactivateCorporateModule };
    },
    UpdateCorporateRcaEnabledOsornoBO: () => {
        const [updateCorporateRcaEnabledMutation, { error }] = useMutation(updateCorporateRcaEnabled);
        return { apolloMutationError: error, updateCorporateRcaEnabled: updateCorporateRcaEnabledMutation };
    },
    GetCorporationDetail: (corporate_id: number) => {
        const { loading, data, error, refetch } = useQuery(getCorporationDetail, {
            fetchPolicy: "no-cache",
            variables: { corporate_id },
        });
        const [corporate, setCorporate] = useState<ICorporation | undefined>(undefined);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);
        const [millsWithNoDivision, setMillsWithNoDivision] = useState<IMill[]>([]);

        useEffect(() => {
            if (data?.corporate) {
                const corporationObject: ICorporation = data?.corporate?.[0];
                setCorporate(corporationObject);
            }
            if (data?.mill) {
                const millsArray: IMill[] = data.mill;
                setMillsWithNoDivision(millsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);
        return {
            loading,
            corporate,
            millsWithNoDivision,
            error: apolloQueryError,
            refetch,
        };
    },
    GetCorporationAdmins: (corporate_id: number, user_type_id: number) => {
        const { loading, data, error, refetch } = useQuery(GetCorporationAdmins, {
            variables: { corporate_id, user_type_id },
        });

        let corporationAdmins: IUser[] = [];

        if (data) {
            corporationAdmins = data?.corporate?.[0].users;
        }

        return {
            loading,
            corporationAdmins,
            error,
            refetch,
        };
    },
    GetDowntimeSummaryByFailureTypeCorporateDashboard: (filters: ICorporateDashboardFilters, corporateId: number) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryByFailureTypeCorporateDashboardResponse[];
        }>(getDowntimeSummaryByFailureTypeCorporateDashboard, {
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_corporation_id: corporateId,
                    p_division_id:
                        filters.division && filters.division !== allElements ? (filters.division as number) : null,
                    p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
                    p_production_line_ids:
                        filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                },
            },
        });
        const [downtimeSummaryByFailureType, setDowntimeSummaryByFailureType] = useState<
            IDowntimeSummaryByFailureType[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryByFailureTypeArray: IDowntimeSummaryByFailureType[] = data.downtimeSummary.map(
                    (item) => ({
                        failure_type_id: item.failure_type_id,
                        name: item.failure_type.name,
                        color_code: item.failure_type.color_code,
                        is_location_specific: item.failure_type.is_location_specific || false,
                        default_failure_type_translations: item.failure_type.default_failure_type_translations || [],
                        failure_codes: [],
                        event_failure_code_summary_per_mcs_aggregate: {
                            aggregate: {
                                sum: {
                                    seconds: item.total_seconds,
                                    occurrences: item.total_occurrences,
                                },
                            },
                        },
                    }),
                );
                setDowntimeSummaryByFailureType(downtimeSummaryByFailureTypeArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureType, apolloQueryError, refetch };
    },
    GetDowntimeSummaryByFailureCodeCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        corporateId: number,
        failureTypeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryByFailureCodeCorporateDashboardResponse[];
        }>(getDowntimeSummaryByFailureCodeCorporateDashboardFiltered, {
            variables: {
                ...(failureTypeId && {
                    where: {
                        failure_code_id: { _is_null: false },
                    },
                }),
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_corporation_id: corporateId,
                    p_failure_type_id: failureTypeId || null,
                    p_division_id:
                        filters.division && filters.division !== allElements ? (filters.division as number) : null,
                    p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
                    p_production_line_ids:
                        filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                },
            },
        });
        const [downtimeSummaryByFailureCode, setDowntimeSummaryByFailureCode] = useState<
            IDowntimeSummaryByFailureCode[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryByFailureCodeArray: IDowntimeSummaryByFailureCode[] = data.downtimeSummary
                    .filter((summary) => summary.total_seconds > 0)
                    .map((item) => ({
                        failure_code_id: item.failure_code_id,
                        name: item.failure_code.name,
                        default_failure_code_translations: item.failure_code.default_failure_code_translations || [],
                        failure_type: {
                            color_code: item.failure_code.failure_type.color_code,
                        },
                        event_failure_code_summary_per_mcs_aggregate: {
                            aggregate: {
                                sum: {
                                    seconds: item.total_seconds,
                                    occurrences: item.total_occurrences,
                                },
                            },
                        },
                    }));
                setDowntimeSummaryByFailureCode(downtimeSummaryByFailureCodeArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, downtimeSummaryByFailureCode, apolloQueryError, refetch };
    },
    GetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        corporateId: number,
        failureCodeId?: number,
    ) => {
        const { loading, error, data, refetch } = useQuery<{
            downtimeSummary: IGetDowntimeSummaryBySecondaryFailureCodeCorporateDashboardResponse[];
        }>(getDowntimeSummaryBySecondaryFailureCodeCorporateDashboardFiltered, {
            skip: !failureCodeId,
            variables: {
                args: {
                    p_start_date: getStartOfDateRange(filters.startDate as Date),
                    p_end_date: getEndOfDateRange(filters.endDate as Date),
                    p_corporation_id: corporateId,
                    p_failure_code_id: failureCodeId || null,
                    p_division_id:
                        filters.division && filters.division !== allElements ? (filters.division as number) : null,
                    p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
                    p_production_line_ids:
                        filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                            ? `{${filters.productionLine}}`
                            : null,
                    p_machine_center_ids:
                        filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                            ? `{${filters.machineCenter}}`
                            : null,
                },
            },
        });
        const [downtimeSummaryBySecondaryFailureCodeTotals, setDowntimeSummaryBySecondaryFailureCodeTotals] = useState<
            IDowntimeSummaryBySecondaryFailureCodeTotals[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.downtimeSummary) {
                const downtimeSummaryBySecondaryFailureCodeArray = data.downtimeSummary.filter(
                    (summary) => summary.total_seconds > 0,
                );
                setDowntimeSummaryBySecondaryFailureCodeTotals(downtimeSummaryBySecondaryFailureCodeArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryBySecondaryFailureCodeTotals,
            apolloQueryError,
            refetch,
        };
    },
    GetTotalDowntimeSummaryByTypeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        corporateId: number,
        id?: number,
    ) => {
        const startDateTrailingTrend =
            TrailingTrendPeriodOptionsEnum.ONE_MONTH === filters.trailingTrendPeriod
                ? moment
                      .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                      .subtract(1, "months")
                      .startOf("day")
                      .toDate()
                : TrailingTrendPeriodOptionsEnum.THIRTEEN_WEEK === filters.trailingTrendPeriod
                  ? moment
                        .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                        .subtract(13, "weeks")
                        .startOf("day")
                        .toDate()
                  : TrailingTrendPeriodOptionsEnum.SIX_MONTH === filters.trailingTrendPeriod
                    ? moment
                          .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                          .subtract(6, "months")
                          .startOf("day")
                          .toDate()
                    : moment
                          .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                          .subtract(1, "years")
                          .startOf("day")
                          .toDate();

        const baseArgs = {
            p_start_date: getStartOfDateRange(filters.startDate as Date),
            p_end_date: getEndOfDateRange(filters.endDate as Date),
            p_corporation_id: corporateId,
            p_division_id: filters.division && filters.division !== allElements ? (filters.division as number) : null,
            p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
            p_production_line_ids:
                filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                    ? `{${filters.productionLine}}`
                    : null,
            p_machine_center_ids:
                filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                    ? `{${filters.machineCenter}}`
                    : null,
            p_failure_type_id: summaryOption === SummaryOption.BY_TYPE && id ? id : null,
        };

        const trailingArgs = {
            ...baseArgs,
            p_start_date: getStartOfDateRange(startDateTrailingTrend),
            p_end_date: getEndOfDateRange(filters.startDate as Date),
        };

        const { loading, error, data, refetch } = useQuery<{
            baseDowntimes: IGetDowntimeSummaryFailureTypeByMillsCorporateDashboardFilteredResponse[];
            trailingTrendDowntimes: IGetDowntimeSummaryFailureTypeByMillsCorporateDashboardFilteredResponse[];
        }>(getDowntimeSummaryFailureTypeByMillsCorporateDashboardFiltered, {
            variables: {
                args: baseArgs,
                trailingArgs: trailingArgs,
            },
        });
        const [downtimeSummaryAllMills, setDowntimeSummaryAllMills] = useState<IDowntimeSummaryTotalsByTypeAllMills[]>(
            [],
        );
        const [downtimeSummaryTrailingPeriod, setDowntimeSummaryTrailingPeriod] = useState<
            IDowntimeSummaryTotalsByTypeAllMills[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                // Transform to match the expected interface structure; exclude soft-deleted mills (mill can be null)
                const downtimeSummaryByFailureTypeAllMillsArray = data.baseDowntimes.filter(
                    (summary) =>
                        summary.total_seconds > 0 &&
                        summary.mill != null &&
                        summary.mill.mill_id != null,
                );
                setDowntimeSummaryAllMills(downtimeSummaryByFailureTypeAllMillsArray);

                if (data?.trailingTrendDowntimes) {
                    // Transform to match the expected interface structure; exclude soft-deleted mills
                    const downtimeSummaryTrailingPeriodArray = data.trailingTrendDowntimes.filter(
                        (summary) =>
                            summary.total_seconds > 0 &&
                            summary.mill != null &&
                            summary.mill.mill_id != null,
                    );
                    setDowntimeSummaryTrailingPeriod(downtimeSummaryTrailingPeriodArray);
                }
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return {
            loading,
            downtimeSummaryAllMills,
            downtimeSummaryTrailingPeriod,
            apolloQueryError,
            refetch,
        };
    },
    GetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        corporateId: number,
        codeId?: number,
        secondaryCodeId?: number,
    ) => {
        const startDateTrailingTrend =
            TrailingTrendPeriodOptionsEnum.ONE_MONTH === filters.trailingTrendPeriod
                ? moment
                      .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                      .subtract(1, "months")
                      .startOf("day")
                      .toDate()
                : TrailingTrendPeriodOptionsEnum.THIRTEEN_WEEK === filters.trailingTrendPeriod
                  ? moment
                        .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                        .subtract(13, "weeks")
                        .startOf("day")
                        .toDate()
                  : TrailingTrendPeriodOptionsEnum.SIX_MONTH === filters.trailingTrendPeriod
                    ? moment
                          .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                          .subtract(6, "months")
                          .startOf("day")
                          .toDate()
                    : moment
                          .utc(filters.startDate ? new Date(filters.startDate) : new Date())
                          .subtract(1, "years")
                          .startOf("day")
                          .toDate();

        const baseArgs =
            summaryOption === SummaryOption.BY_CODE
                ? {
                      p_start_date: getStartOfDateRange(filters.startDate as Date),
                      p_end_date: getEndOfDateRange(filters.endDate as Date),
                      p_corporation_id: corporateId,
                      p_failure_code_id: codeId || null,
                      p_division_id:
                          filters.division && filters.division !== allElements ? (filters.division as number) : null,
                      p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
                      p_production_line_ids:
                          filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                              ? `{${filters.productionLine}}`
                              : null,
                      p_machine_center_ids:
                          filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                              ? `{${filters.machineCenter}}`
                              : null,
                  }
                : {
                      p_start_date: getStartOfDateRange(filters.startDate as Date),
                      p_end_date: getEndOfDateRange(filters.endDate as Date),
                      p_corporation_id: corporateId,
                      p_failure_code_id: codeId || null,
                      p_secondary_failure_code_id: secondaryCodeId || null,
                      p_division_id:
                          filters.division && filters.division !== allElements ? (filters.division as number) : null,
                      p_region_id: filters.region && filters.region !== allElements ? (filters.region as number) : null,
                      p_production_line_ids:
                          filters.productionLine?.length! > 0 && !filters.productionLine?.includes(allElements)
                              ? `{${filters.productionLine}}`
                              : null,
                      p_machine_center_ids:
                          filters.machineCenter?.length! > 0 && !filters.machineCenter?.includes(allElements)
                              ? `{${filters.machineCenter}}`
                              : null,
                  };

        const trailingArgs = {
            ...baseArgs,
            p_start_date: getStartOfDateRange(startDateTrailingTrend),
            p_end_date: getEndOfDateRange(filters.startDate as Date),
        };

        const { loading, error, data, refetch } = useQuery<{
            baseDowntimes: IGetDowntimeSummaryFailureCodeByMillsCorporateDashboardFilteredResponse[];
            trailingTrendDowntimes: IGetDowntimeSummaryFailureCodeByMillsCorporateDashboardFilteredResponse[];
        }>(
            summaryOption === SummaryOption.BY_CODE
                ? getDowntimeSummaryFailureCodeByMillsCorporateDashboardFiltered
                : getDowntimeSummarySecondaryFailureCodeByMillsCorporateDashboardFiltered,
            {
                skip: summaryOption === SummaryOption.BY_SECONDARY_CODE ? !secondaryCodeId || !codeId : !codeId,
                variables: {
                    args: baseArgs,
                    trailingArgs: trailingArgs,
                },
            },
        );
        const [downtimeSummaryAllMills, setDowntimeSummaryAllMills] = useState<IDowntimeSummaryTotalsByCodeAllMills[]>(
            [],
        );
        const [downtimeSummaryTrailingPeriod, setDowntimeSummaryTrailingPeriod] = useState<
            IDowntimeSummaryTotalsByCodeAllMills[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.baseDowntimes) {
                // Exclude soft-deleted mills (mill can be null from API)
                const downtimeSummaryByFailureTypeAllMillsArray = data.baseDowntimes.filter(
                    (summary) =>
                        summary.total_seconds > 0 &&
                        summary.mill != null &&
                        summary.mill.mill_id != null,
                );
                setDowntimeSummaryAllMills(downtimeSummaryByFailureTypeAllMillsArray);

                if (data?.trailingTrendDowntimes) {
                    const downtimeSummaryTrailingPeriodArray = data.trailingTrendDowntimes.filter(
                        (summary) =>
                            summary.total_seconds > 0 &&
                            summary.mill != null &&
                            summary.mill.mill_id != null,
                    );
                    setDowntimeSummaryTrailingPeriod(downtimeSummaryTrailingPeriodArray);
                }
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error, summaryOption]);

        return {
            loading,
            downtimeSummaryAllMills,
            downtimeSummaryTrailingPeriod,
            apolloQueryError,
            refetch,
        };
    },
    GetCorporateAllDivisionAllRegionsAllMills: () => {
        const { loading, error, data, refetch } = useQuery(getCorporateAllDivisionAllRegionsAllMills);
        const [corporations, setCorporations] = useState<ICorporation[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.corporate) {
                const corporationsArray: ICorporation[] = data.corporate;
                setCorporations(corporationsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, corporations, error: apolloQueryError, refetch };
    },
    GetAllKeySuccessFactors: () => {
        const { loading, error, data } = useQuery(getAllKeySuccessFactors);
        const [keySuccessFactors, setKeySuccessFactors] = useState<IKeySuccessFactor[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.key_success_factors) {
                const keySuccessFactorsArray: IKeySuccessFactor[] = data.key_success_factors;
                setKeySuccessFactors(keySuccessFactorsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, keySuccessFactors, error: apolloQueryError };
    },
    GetAllFailureTypes: () => {
        const { loading, error, data } = useQuery(getAllFailureTypes);
        const [failureTypes, setFailureTypes] = useState<IFailureType[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data?.failure_type) {
                const failureTypesArray: IFailureType[] = data.failure_type;
                setFailureTypes(failureTypesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, failureTypes, error: apolloQueryError };
    },
    UpdateCorporateDashboardFeature: () => {
        const [updateCorporateDashboard, { error, loading }] = useMutation(updateCorporateDashboardFeature);

        const activateCorporateDashboard = useCallback(
            async (corporateId: number) =>
                await updateCorporateDashboard({
                    variables: {
                        corporate_id: corporateId,
                        corporate_dashboard_enabled: true,
                    },
                }),
            [updateCorporateDashboard],
        );

        const deactivateCorporateDashboard = useCallback(
            async (corporateId: number) =>
                await updateCorporateDashboard({
                    variables: {
                        corporate_id: corporateId,
                        corporate_dashboard_enabled: false,
                    },
                }),
            [updateCorporateDashboard],
        );

        return { error, activateCorporateDashboard, deactivateCorporateDashboard, loading };
    },
};
